import { Button, Table } from 'antd';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import React from 'react';
import { ICredit } from 'src/apis/Reporting/Credits';
import CreditsCell from './CreditsCell';
import { FormContext } from './CreditsRow';
import CreditsRow from './CreditsRow';

interface ILocalProps {
    credits:  ICredit[];
    loading: boolean;
    handleDelete: (id: string) => void,
    updateID: (id: string, credit: ICredit, note: string) => void,
    handleSaveCredit: (credit: ICredit, id: string) => any;
}

interface ILocalState {
    editingId: string;
}

class CreditsTable extends React.Component<ILocalProps, ILocalState> {
    public state = { editingId: '0' };

    public componentDidUpdate(prevProps: ILocalProps, prevState: ILocalState) {
        if (this.props.credits.length !== prevProps.credits.length) {
            this.setState({ editingId: `newCredit${this.props.credits.length}` });
        }
    }

    public isEditing = (id: string) => () => {
        return id === this.state.editingId;
    };

    public edit = (id: string) => () => {
        this.setState({ editingId: id });
    };

    public save = (form: WrappedFormUtils | null, id: string) => () => {
        form!.validateFields(async (error, credit: ICredit) => {
            if (error) {
                return;
            }

            // save the credit info
            const updatedCredit: any = await this.props.handleSaveCredit(credit, id);
            if (updatedCredit) {
                this.props.updateID(id, updatedCredit, credit.note);
                this.setState({ editingId: '0' });
                return;
            }

            this.cancel(id);
        });
    };

    public cancel = (id: string) => () => {
        if (id.indexOf('newCredit') !== -1) {
            this.props.handleDelete(id);
        } else {
            this.setState({ editingId: '0' });
        }
    };

    public render() {
        const columns = [
            {
                dataIndex: 'createTimestamp',
                editable: false,
                title: 'Date',
                width: '15%',
            },
            {
                dataIndex: 'creditAmount',
                editable: true,
                title: 'Amount',
                width: '8%',
            },
            {
                dataIndex: 'creditBalance',
                editable: true,
                title: 'Balance',
                width: '8%',
            },
            {
                dataIndex: 'spread',
                editable: true,
                title: 'Spread',
                width: '8%',
            },
            {
                dataIndex: 'childCompanyProfileID',
                editable: true,
                title: 'Client Company ID',
                width: '20%',
            },
            {
                dataIndex: 'note',
                editable: true,
                title: 'Note',
                width: '33%',
            },
            {
                align: 'right' as 'right',
                dataIndex: 'action',
                render: (text: string, record: ICredit) => {
                    const editable = this.isEditing(record.id.toString())();
                    return (
                        <div>
                            {editable ? (
                                <span>
                                    <FormContext.Consumer>
                                        {(form: WrappedFormUtils | null) => (
                                          <Button type={'link'} onClick={this.save(form, record.id.toString())}>
                                              Save
                                          </Button>
                                        )}
                                    </FormContext.Consumer>
                                </span>
                            ) : (
                              <Button type={'link'} onClick={this.edit(record.id.toString())}>Edit</Button>
                            )}
                        </div>
                    );
                },
                title: 'Action',
                width: '8%',
            },
        ];

        const components = {
            body: {
                cell: CreditsCell,
                row: CreditsRow,
            },
        };

        const updatedColumns = columns.map((column) => {
            if (!column.editable) {
                return column;
            }
            return {
                ...column,
                onCell: (record: ICredit) => ({
                    dataIndex: column.dataIndex,
                    editing: this.isEditing(record.id.toString())(),
                    inputtype: column.dataIndex === 'note' ? 'text' : 'number',
                    record,
                    title: column.title,
                }),
            };
        });

        return (
            <Table
                components={components}
                bordered={true}
                dataSource={this.props.credits}
                columns={updatedColumns}
                rowKey='id'
                loading={this.props.loading}
                pagination={false}
            />
        );
    }
}

export default CreditsTable;
